import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { documentsApi, documentTypesApi } from '../api';
import type { Document, DocumentType } from '../types';
import { useSettings } from '../context/SettingsContext';
import { useSidebarResize } from '../hooks/useSidebarResize';
import {
  FileText, Upload, RefreshCw, Trash2, AlertTriangle, X,
  Check, Eye, ExternalLink, Pencil, Download, User,
} from 'lucide-react';
import { ExtractedFieldsEditor } from '../components/ExtractedFieldsEditor';
import {
  Header, SearchBar, Initials,
  TYPE_COLORS, STATUS_CONFIG,
} from '../components/ui';
import { timeAgo } from '../utils';

export function DocumentsPage() {
  const { formatDate, settings } = useSettings();
  const [searchParams, setSearchParams] = useSearchParams();
  const [documents, setDocuments] = useState<Document[]>([]);
  const [docTypes, setDocTypes] = useState<DocumentType[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{ current: number; total: number } | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [selectedTypeId, setSelectedTypeId] = useState<string | null>(null);
  const [selectedSousType, setSelectedSousType] = useState<string>('');
  const [selectedStatus, setSelectedStatus] = useState<string>('');
  const [isDrag, setIsDrag] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState<Document | null>(null);
  const [loadingDoc, setLoadingDoc] = useState(false);
  const [changingType, setChangingType] = useState(false);
  const [newTypeId, setNewTypeId] = useState('');
  const [savingType, setSavingType] = useState(false);
  const [changingSousType, setChangingSousType] = useState(false);
  const [newSousType, setNewSousType] = useState('');
  const [savingSousType, setSavingSousType] = useState(false);
  const [validating, setValidating] = useState(false);
  const [reprocessing, setReprocessing] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalDocs, setTotalDocs] = useState(0);
  const PAGE_SIZE = 20;
  const { width: sidebarWidth, handleMouseDown: handleSidebarResize, isResizing } = useSidebarResize('docs-sidebar-width')
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollDetailRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const bgPollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const handleExport = () => {
    const STATUS_LABELS: Record<string, string> = {
      en_cours: 'En cours', traite: 'Traité', a_verifier: 'À vérifier',
      erreur: 'Erreur', archive: 'Archivé', supprime: 'Supprimé',
    };

    const rows: string[][] = [
      ['Nom du fichier', 'Type', 'Collaborateur', 'Statut', 'Confiance (%)', 'Expiration RGPD', 'Date import'],
      ...documents.map(d => [
        d.original_filename,
        d.document_type?.label ?? '',
        d.collaborateur?.nom_complet ?? '',
        STATUS_LABELS[d.status] ?? d.status,
        d.classification_confidence != null ? String(Math.round(d.classification_confidence * 100)) : '',
        d.date_expiration ? new Date(d.date_expiration).toLocaleDateString('fr-FR') : '',
        new Date(d.created_at).toLocaleDateString('fr-FR'),
      ]),
    ];

    const csv = rows
      .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(';'))
      .join('\n');

    const bom = '\uFEFF'; // UTF-8 BOM pour Excel
    const blob = new Blob([bom + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const date = new Date().toISOString().slice(0, 10);
    a.href = url;
    a.download = `acodoc_export_${date}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const reloadDoc = (id: string) =>
    documentsApi.get(id).then(r => {
      setSelectedDoc(r.data);
      setDocuments(prev => prev.map(d => d.id === id ? r.data : d));
    });

  const handleSelectDoc = (doc: Document) => {
    setLoadingDoc(true);
    setChangingType(false);
    setSearchParams(p => { p.set('doc', doc.id); return p; });
    documentsApi.get(doc.id)
      .then(r => setSelectedDoc(r.data))
      .finally(() => setLoadingDoc(false));
  };

  const handleCloseDoc = () => {
    setSelectedDoc(null);
    setSearchParams(p => { p.delete('doc'); return p; });
  };

  const handlePreview = async (e: React.MouseEvent, docId: string) => {
    e.stopPropagation();
    try {
      const res = await documentsApi.getPreviewUrl(docId);
      const blob = new Blob([res.data], { type: res.headers['content-type'] as string | undefined });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch {
      alert("Impossible d'ouvrir le document.");
    }
  };

  const handleReprocess = (docId: string) => {
    setReprocessing(true);
    documentsApi.reprocess(docId)
      .then(() => {
        load(true);
        startPolling();
        if (pollDetailRef.current) clearInterval(pollDetailRef.current);
        pollDetailRef.current = setInterval(() => {
          documentsApi.get(docId).then(r => {
            setSelectedDoc(r.data);
            if (r.data.status !== 'en_cours') {
              clearInterval(pollDetailRef.current!);
              pollDetailRef.current = null;
            }
          });
        }, 3000);
      })
      .finally(() => setReprocessing(false));
  };

  const handleValidate = (docId: string) => {
    setValidating(true);
    documentsApi.validate(docId)
      .then(() => {
        // Mise à jour optimiste : affiche immédiatement le nouveau statut
        setDocuments(prev => prev.map(d => d.id === docId ? { ...d, status: 'traite' as const } : d));
        setSelectedDoc(prev => prev?.id === docId ? { ...prev, status: 'traite' as const } : prev);
        reloadDoc(docId);
        load(true);
      })
      .finally(() => setValidating(false));
  };

  const handleSaveType = (docId: string) => {
    if (!newTypeId) return;
    setSavingType(true);
    documentsApi.update(docId, { document_type_id: newTypeId })
      .then(() => { reloadDoc(docId); load(true); })
      .finally(() => { setSavingType(false); setChangingType(false); setNewTypeId(''); });
  };

  const handleSaveSousType = (docId: string) => {
    setSavingSousType(true);
    documentsApi.update(docId, { sous_type: newSousType.trim() || '' })
      .then(() => { reloadDoc(docId); load(true); })
      .finally(() => { setSavingSousType(false); setChangingSousType(false); setNewSousType(''); });
  };

  // Sous-types disponibles parmi les documents chargés pour le type sélectionné
  const availableSousTypes = Array.from(
    new Set(
      documents
        .map(d => d.metadata_extra?.sous_type)
        .filter((s): s is string => !!s)
    )
  ).sort();

  // Filtrage client-side par sous_type
  const filteredDocuments = selectedSousType
    ? documents.filter(d => d.metadata_extra?.sous_type === selectedSousType)
    : documents;

  const load = (silent = false) => {
    if (!silent) setLoading(true);
    const params: Record<string, any> = { size: PAGE_SIZE, page: currentPage };
    if (selectedTypeId) params.type_id = selectedTypeId;
    if (selectedStatus) params.status = selectedStatus;
    if (search) params.search = search;
    documentsApi.list(params)
      .then(r => {
        setDocuments(r.data.items);
        setTotalDocs(r.data.total);
        setTotalPages(r.data.pages);
        // Si un document est ouvert dans le panneau et que son statut a changé,
        // on le met à jour directement pour éviter de rester bloqué sur "En cours".
        setSelectedDoc(prev => {
          if (!prev) return prev;
          const updated = r.data.items.find((d: Document) => d.id === prev.id);
          if (updated && updated.status !== prev.status) return updated;
          return prev;
        });
        const hasPending = r.data.items.some(
          (d: Document) => d.status === 'en_cours'
        );
        if (hasPending && !pollRef.current) {
          pollRef.current = setInterval(() => load(true), 4000);
        } else if (!hasPending && pollRef.current) {
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
      })
      .finally(() => { if (!silent) setLoading(false); });
  };

  const startPolling = () => {
    if (pollRef.current) return;
    pollRef.current = setInterval(() => load(true), 4000);
  };

  useEffect(() => {
    documentTypesApi.list().then(r => setDocTypes(r.data));
    // Restaurer le doc sélectionné depuis l'URL après un refresh
    const docIdFromUrl = searchParams.get('doc');
    if (docIdFromUrl) {
      setLoadingDoc(true);
      documentsApi.get(docIdFromUrl)
        .then(r => setSelectedDoc(r.data))
        .catch(() => setSearchParams(p => { p.delete('doc'); return p; }))
        .finally(() => setLoadingDoc(false));
    }
  }, []);

  useEffect(() => {
    setSelectedSousType(''); // reset sous-type quand le type principal change
  }, [selectedTypeId]);

  useEffect(() => {
    load();
    bgPollRef.current = setInterval(() => load(true), 5000);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      if (pollDetailRef.current) clearInterval(pollDetailRef.current);
      if (bgPollRef.current) clearInterval(bgPollRef.current);
    };
  }, [currentPage, selectedTypeId, selectedStatus, search]);

  const handleUploadMany = async (files: FileList | File[]) => {
    const list = Array.from(files);
    if (list.length === 0) return;
    setUploading(true);
    setUploadError(null);
    setUploadProgress({ current: 0, total: list.length });
    const duplicates: string[] = [];
    const failed: string[] = [];
    // Séquentiel : évite de saturer le worker Celery et rend les conflits 409 lisibles.
    for (let i = 0; i < list.length; i++) {
      const file = list[i];
      setUploadProgress({ current: i + 1, total: list.length });
      try {
        await documentsApi.upload(file);
      } catch (err: any) {
        if (err?.response?.status === 409) duplicates.push(file.name);
        else failed.push(file.name);
      }
    }
    load();
    startPolling();
    const messages: string[] = [];
    if (duplicates.length) messages.push(`Doublon${duplicates.length > 1 ? 's' : ''} ignoré${duplicates.length > 1 ? 's' : ''} : ${duplicates.join(', ')}`);
    if (failed.length) messages.push(`Échec${failed.length > 1 ? 's' : ''} : ${failed.join(', ')}`);
    setUploadError(messages.length ? messages.join(' · ') : null);
    setUploading(false);
    setUploadProgress(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDrag(false);
    if (e.dataTransfer.files.length > 0) handleUploadMany(e.dataTransfer.files);
  };

  return (
    <div className="flex flex-1 overflow-hidden">
      <div className="flex-1 overflow-y-auto">
        <Header title=" Documents">
          <SearchBar placeholder="Rechercher un document..." value={search} onChange={setSearch} />
          <button
            onClick={handleExport}
            disabled={documents.length === 0}
            className="border border-gray-200 bg-white text-gray-700 px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
            title={documents.length === 0 ? 'Aucun document à exporter' : `Exporter ${documents.length} document(s) en CSV`}
          >
            <Download className="w-4 h-4" /> Exporter
          </button>
          <button
            onClick={() => !uploading && fileInputRef.current?.click()}
            disabled={uploading}
            className="bg-teal-600 hover:bg-teal-700 disabled:bg-teal-400 text-white px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-2 transition"
          >
            {uploading
              ? <><RefreshCw className="w-4 h-4 animate-spin" /> Traitement...</>
              : <><Upload className="w-4 h-4" /> Importer</>
            }
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.jpg,.jpeg,.png,.tiff"
            multiple
            className="hidden"
            onChange={e => { if (e.target.files && e.target.files.length > 0) handleUploadMany(e.target.files); }}
          />
        </Header>

        <div className="p-6 space-y-6">
          <div
            className={`border-2 border-dashed rounded-2xl p-6 sm:p-10 text-center transition-all cursor-pointer ${uploading ? 'border-teal-400 bg-teal-50' : isDrag ? 'border-teal-400 bg-teal-50' : 'border-gray-300 bg-gray-50 hover:border-teal-300'}`}
            onClick={() => !uploading && fileInputRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); if (!uploading) setIsDrag(true); }}
            onDragLeave={() => setIsDrag(false)}
            onDrop={handleDrop}
          >
            {uploading
              ? <><RefreshCw className="w-10 h-10 mx-auto mb-3 text-teal-500 animate-spin" /><p className="text-base font-semibold text-teal-700">{uploadProgress && uploadProgress.total > 1 ? `Upload ${uploadProgress.current}/${uploadProgress.total}, traitement IA lancé...` : 'Upload en cours, traitement IA lancé...'}</p></>
              : <><Upload className={`w-10 h-10 mx-auto mb-3 ${isDrag ? 'text-teal-500' : 'text-gray-400'}`} /><p className="text-base font-semibold text-gray-700">Glissez-déposez vos documents ici</p><p className="text-sm text-gray-400 mt-1">ou cliquez pour sélectionner un ou plusieurs fichiers • PDF, JPG, PNG acceptés</p></>
            }
          </div>

          {uploadError && (
            <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700 flex items-center justify-between">
              <span>{uploadError}</span>
              <button onClick={() => setUploadError(null)} className="text-red-400 hover:text-red-600 ml-3"><X className="w-4 h-4" /></button>
            </div>
          )}

          <div className="flex flex-wrap items-center gap-3">
            <select
              value={selectedTypeId ?? ''}
              onChange={e => { setSelectedTypeId(e.target.value || null); setCurrentPage(1); }}
              className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm bg-white"
            >
              <option value="">Tous les types</option>
              {docTypes.map(t => (
                <option key={t.id} value={t.id}>{t.label}</option>
              ))}
            </select>
            {selectedTypeId && availableSousTypes.length > 0 && (
              <select
                value={selectedSousType}
                onChange={e => setSelectedSousType(e.target.value)}
                className="border border-teal-300 rounded-lg px-3 py-1.5 text-sm bg-white text-teal-700"
              >
                <option value="">Toutes les sous-catégories</option>
                {availableSousTypes.map(st => (
                  <option key={st} value={st}>{st}</option>
                ))}
              </select>
            )}
            <select
              value={selectedStatus}
              onChange={e => { setSelectedStatus(e.target.value); setCurrentPage(1); }}
              className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm bg-white"
            >
              <option value="">Tous les statuts</option>
              <option value="en_cours">En cours</option>
              <option value="traite">Traité</option>
              <option value="a_verifier">À vérifier</option>
              <option value="erreur">Erreur</option>
              <option value="archive">Archivé</option>
            </select>
            <span className="ml-auto text-sm text-gray-500 whitespace-nowrap">
              {totalDocs} document{totalDocs !== 1 ? 's' : ''}
            </span>
          </div>

          {loading ? (
            <div className="flex justify-center py-16">
              <RefreshCw className="w-8 h-8 text-gray-300 animate-spin" />
            </div>
          ) : filteredDocuments.length === 0 ? (
            <div className="text-center py-16 bg-gray-50 rounded-xl border border-dashed border-gray-200">
              <FileText className="w-12 h-12 text-gray-200 mx-auto mb-3" />
              <p className="text-gray-500 font-semibold">Aucun document</p>
              <p className="text-sm text-gray-400 mt-1">{selectedTypeId || selectedStatus || selectedSousType ? 'Aucun résultat pour ce filtre.' : 'Importez votre premier document ci-dessus.'}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4">
              {filteredDocuments.map((doc) => {
                const typeCode = doc.document_type?.code ?? 'autre';
                const typeLabel = doc.document_type?.label ?? 'Autre';
                const sousType = doc.metadata_extra?.sous_type;
                const tc = TYPE_COLORS[typeCode] || TYPE_COLORS.autre;
                const sc = STATUS_CONFIG[doc.status] || STATUS_CONFIG.en_cours;
                const isFilteredByType = Boolean(selectedTypeId || selectedSousType);
                const collabName = doc.collaborateur?.nom_complet;
                return (
                  <div key={doc.id} onClick={() => handleSelectDoc(doc)} className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden hover:shadow-md transition group cursor-pointer">
                    <div className={`h-16 ${tc.bg} flex items-center px-5 gap-3`}>
                      {isFilteredByType ? (
                        <>
                          <User className={`w-6 h-6 ${tc.text} opacity-60`} />
                          <div className="min-w-0">
                            <span className={`text-sm font-bold ${tc.text} uppercase tracking-wide truncate block`}>
                              {collabName ?? 'Non associé'}
                            </span>
                          </div>
                        </>
                      ) : (
                        <>
                          <FileText className={`w-6 h-6 ${tc.text} opacity-60`} />
                          <div>
                            <span className={`text-xs font-bold ${tc.text} uppercase tracking-wide`}>{typeLabel}</span>
                            {sousType && (
                              <p className={`text-xs ${tc.text} opacity-70 mt-0.5`}>{sousType}</p>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                    <div className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          {isFilteredByType ? (
                            <>
                              <p className="font-bold text-gray-900 text-sm">{typeLabel}</p>
                              {sousType && (
                                <p className="text-xs text-gray-500 mt-0.5">{sousType}</p>
                              )}
                              <p className="text-xs text-gray-400 mt-1">{doc.original_filename}</p>
                              <p className="text-xs text-gray-400 mt-0.5">Importé {timeAgo(doc.created_at)}</p>
                            </>
                          ) : (
                            <>
                              <p className="font-bold text-gray-900 text-sm">{doc.original_filename}</p>
                              <p className="text-xs text-gray-400 mt-0.5">Importé {timeAgo(doc.created_at)}</p>
                              {doc.collaborateur && (
                                <p className="text-xs text-teal-600 mt-0.5 font-medium">
                                  {doc.collaborateur.nom_complet}
                                </p>
                              )}
                            </>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {doc.ocr_confidence != null && doc.ocr_confidence * 100 < (settings?.ocr_threshold ?? 80) && (
                            <span
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-red-50 text-red-700 border border-red-200"
                              title={`Qualité image ${Math.round(doc.ocr_confidence * 100)}% — merci de fournir un scan plus net`}
                            >
                              <AlertTriangle className="w-3 h-3" /> Image floue
                            </span>
                          )}
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${sc.color}`}>{sc.label}</span>
                          <span className="text-sm font-bold text-gray-500">
                            {doc.classification_confidence ? `${Math.round(doc.classification_confidence * 100)}%` : '—'}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center justify-end">
                        <div className="flex gap-1 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition">
                          <button
                            onClick={e => handlePreview(e, doc.id)}
                            className="p-1.5 hover:bg-blue-50 rounded-lg group/eye" title="Visualiser"
                          >
                            <Eye className="w-3.5 h-3.5 text-gray-400 group-hover/eye:text-blue-500" />
                          </button>
                          <button
                            onClick={e => { e.stopPropagation(); documentsApi.reprocess(doc.id).then(() => { load(true); startPolling(); }); }}
                            className="p-1.5 hover:bg-gray-100 rounded-lg" title="Retraiter"
                          >
                            <RefreshCw className="w-3.5 h-3.5 text-gray-400" />
                          </button>
                          <button
                            onClick={e => { e.stopPropagation(); if (confirm('Supprimer ce document ?')) documentsApi.delete(doc.id).then(() => { if (selectedDoc?.id === doc.id) handleCloseDoc(); load(); }); }}
                            className="p-1.5 hover:bg-red-50 rounded-lg" title="Supprimer"
                          >
                            <Trash2 className="w-3.5 h-3.5 text-red-400" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-1 py-2">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1.5 text-sm rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition"
              >
                ←
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 2)
                .reduce<(number | 'ellipsis')[]>((acc, p, idx, arr) => {
                  if (idx > 0 && p - (arr[idx - 1] as number) > 1) acc.push('ellipsis');
                  acc.push(p);
                  return acc;
                }, [])
                .map((p, idx) =>
                  p === 'ellipsis' ? (
                    <span key={`e-${idx}`} className="px-2 text-gray-400 text-sm">…</span>
                  ) : (
                    <button
                      key={p}
                      onClick={() => setCurrentPage(p as number)}
                      className={`w-8 h-8 text-sm rounded-lg border transition ${
                        currentPage === p
                          ? 'bg-teal-600 text-white border-teal-600 font-semibold'
                          : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      {p}
                    </button>
                  )
                )}
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1.5 text-sm rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition"
              >
                →
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Backdrop mobile panneau détail */}
      {(selectedDoc || loadingDoc) && (
        <div
          className="fixed inset-0 z-30 bg-black/30 lg:hidden"
          onClick={handleCloseDoc}
        />
      )}

      {/* Handle resize — élément flex entre la liste et la sidebar, lg uniquement */}
      {(selectedDoc || loadingDoc) && (
        <div
          onMouseDown={handleSidebarResize}
          className="hidden lg:flex w-2 items-center justify-center cursor-col-resize group flex-shrink-0 bg-transparent hover:bg-teal-50 transition-colors"
          title="Redimensionner le panneau"
        >
          <div className={`w-0.5 h-16 rounded-full transition-colors ${isResizing ? 'bg-teal-500' : 'bg-gray-200 group-hover:bg-teal-400'}`} />
        </div>
      )}

      {/* Detail Panel */}
      {(selectedDoc || loadingDoc) && (
        <div
          className="fixed inset-y-0 right-0 z-40 w-[92vw] sm:w-96 bg-white border-l border-gray-200 overflow-y-auto lg:static lg:inset-auto lg:shrink-0"
          style={{ width: sidebarWidth }}
        >
          <div className="p-5 border-b border-gray-100 flex items-center justify-between">
            <h3 className="font-bold text-gray-900">Détail du document</h3>
            <button onClick={handleCloseDoc} className="p-1 hover:bg-gray-100 rounded-lg">
              <X className="w-4 h-4 text-gray-500" />
            </button>
          </div>
          {loadingDoc ? (
            <div className="flex items-center justify-center py-20">
              <RefreshCw className="w-6 h-6 text-gray-400 animate-spin" />
            </div>
          ) : selectedDoc && (() => {
            const ocrThreshold = settings?.ocr_threshold ?? 80;
            const ocrQualityPct = selectedDoc.ocr_confidence != null ? Math.round(selectedDoc.ocr_confidence * 100) : null;
            const lowQuality = ocrQualityPct != null && ocrQualityPct < ocrThreshold;
            return (
            <>
              <div className="mx-5 mt-5 h-32 bg-gradient-to-br from-blue-50 to-sky-50 rounded-xl border border-blue-100 flex flex-col items-center justify-center gap-3">
                <FileText className="w-10 h-10 text-blue-300" />
                <button
                  onClick={e => handlePreview(e, selectedDoc.id)}
                  className="flex items-center gap-1.5 text-xs font-semibold text-blue-500 hover:underline"
                >
                  <ExternalLink className="w-3.5 h-3.5" /> Ouvrir le document
                </button>
              </div>

              {lowQuality && (
                <div className="mx-5 mt-4 bg-red-50 border border-red-200 rounded-xl p-4">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5 shrink-0" />
                    <div>
                      <p className="text-sm font-bold text-red-800">
                        Qualité d'image insuffisante ({ocrQualityPct}%)
                      </p>
                      <p className="text-xs text-red-700 mt-1">
                        L'image est trop floue pour une lecture fiable (seuil&nbsp;: {ocrThreshold}%).
                        Merci de fournir un scan plus net, puis de retraiter le document.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="p-5 space-y-5">
                {/* Infos fichier */}
                <div>
                  <p className="text-sm font-bold text-gray-900 mb-0.5">{selectedDoc.original_filename}</p>
                  <p className="text-xs text-gray-400">{timeAgo(selectedDoc.created_at)}</p>
                </div>

                {/* Type de document — modifiable */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider">Type de document</h4>
                    {!changingType && (
                      <button
                        onClick={() => { setChangingType(true); setNewTypeId(selectedDoc.document_type_id ?? ''); }}
                        className="flex items-center gap-1 text-xs text-teal-600 hover:text-teal-800"
                      >
                        <Pencil className="w-3 h-3" /> Modifier
                      </button>
                    )}
                  </div>
                  {changingType ? (
                    <div className="flex gap-2">
                      <select
                        value={newTypeId}
                        onChange={e => setNewTypeId(e.target.value)}
                        className="flex-1 border border-gray-200 rounded-lg px-2 py-1.5 text-sm bg-white"
                      >
                        <option value="">— Sélectionner —</option>
                        {docTypes.map(t => (
                          <option key={t.id} value={t.id}>{t.label}</option>
                        ))}
                      </select>
                      <button
                        onClick={() => handleSaveType(selectedDoc.id)}
                        disabled={savingType || !newTypeId}
                        className="p-1.5 bg-teal-600 hover:bg-teal-700 disabled:bg-teal-400 text-white rounded-lg"
                      >
                        {savingType ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                      </button>
                      <button onClick={() => setChangingType(false)} className="p-1.5 hover:bg-gray-100 rounded-lg">
                        <X className="w-3.5 h-3.5 text-gray-400" />
                      </button>
                    </div>
                  ) : (
                    <div>
                      <p className="text-sm font-semibold text-gray-900">
                        {selectedDoc.document_type?.label ?? <span className="text-gray-400 italic">Non détecté</span>}
                      </p>
                      {/* Sous-type — éditable */}
                      <div className="mt-1.5">
                        {changingSousType ? (
                          <div className="flex gap-1.5 items-center">
                            <input
                              type="text"
                              value={newSousType}
                              onChange={e => setNewSousType(e.target.value)}
                              placeholder="Ex : Passeport français"
                              className="flex-1 border border-gray-200 rounded-lg px-2 py-1 text-xs bg-white"
                              autoFocus
                            />
                            <button
                              onClick={() => handleSaveSousType(selectedDoc.id)}
                              disabled={savingSousType}
                              className="p-1 bg-teal-600 hover:bg-teal-700 disabled:bg-teal-400 text-white rounded-lg"
                            >
                              {savingSousType ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                            </button>
                            <button
                              onClick={() => { setChangingSousType(false); setNewSousType(''); }}
                              className="p-1 hover:bg-gray-100 rounded-lg"
                            >
                              <X className="w-3 h-3 text-gray-400" />
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => { setChangingSousType(true); setNewSousType(selectedDoc.metadata_extra?.sous_type ?? ''); }}
                            className="flex items-center gap-1 text-xs text-gray-500 italic hover:text-teal-600 group/st"
                          >
                            <span>{selectedDoc.metadata_extra?.sous_type ?? 'Ajouter une sous-catégorie'}</span>
                            <Pencil className="w-3 h-3 opacity-0 group-hover/st:opacity-100 transition" />
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Collaborateur */}
                <div>
                  <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Collaborateur</h4>
                  {selectedDoc.collaborateur ? (
                    <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                      <Initials nom_complet={selectedDoc.collaborateur.nom_complet} size="sm" />
                      <p className="text-sm font-semibold text-gray-900 uppercase">
                        {selectedDoc.collaborateur.nom_complet}
                      </p>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-400 italic">Non associé</p>
                  )}
                </div>

                {/* Bouton validation manuelle — uniquement pour les docs "À vérifier" */}
                {selectedDoc.status === 'a_verifier' && (
                  <button
                    onClick={() => handleValidate(selectedDoc.id)}
                    disabled={validating}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition text-white bg-teal-600 hover:bg-teal-700 disabled:bg-teal-400"
                  >
                    {validating
                      ? <><RefreshCw className="w-4 h-4 animate-spin" /> Validation...</>
                      : <><Check className="w-4 h-4" /> Valider le document</>
                    }
                  </button>
                )}

                {/* Bouton retraiter */}
                <button
                  onClick={() => handleReprocess(selectedDoc.id)}
                  disabled={reprocessing || selectedDoc.status === 'en_cours'}
                  className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50 disabled:opacity-50 transition"
                >
                  {reprocessing || selectedDoc.status === 'en_cours'
                    ? <><RefreshCw className="w-4 h-4 animate-spin" /> Retraitement en cours...</>
                    : <><RefreshCw className="w-4 h-4" /> Retraiter le document</>
                  }
                </button>

                {/* Données extraites éditables */}
                <div>
                  <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Informations extraites</h4>
                  <ExtractedFieldsEditor
                    docId={selectedDoc.id}
                    extractedData={selectedDoc.extracted_data ?? []}
                    onUpdated={() => reloadDoc(selectedDoc.id)}
                  />
                </div>
              </div>

              {selectedDoc.date_expiration && (
                <div className="mx-5 mb-5 bg-orange-50 border border-orange-200 rounded-xl p-4">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-5 h-5 text-orange-500 mt-0.5 shrink-0" />
                    <div>
                      <p className="text-sm font-bold text-orange-800">Alerte RGPD</p>
                      <p className="text-xs text-orange-700 mt-1">
                        Expiration le {formatDate(selectedDoc.date_expiration)}.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </>
            );
          })()}
        </div>
      )}
    </div>
  );
}
